import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
// Badge available if needed: import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Phone,
  Users,
  TrendingUp,
  Search,
  Activity,
  Headphones,
  PhoneCall,
  Clock,
  DollarSign,
  CheckCircle,
  Award,
  AlertTriangle,
  Filter,
  BarChart3,
  Building2,
  Layers,
  Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
// date-fns available: import { format } from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

// ─── HELPERS ──────────────────────────────────────────────────────────────────

/** Indian Rupee formatting with lakhs/crores notation */
const formatINR = (amount) => {
  if (!amount && amount !== 0) return '--';
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return '--';
  return '\u20B9' + num.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

/** Safe numeric parse - handles strings, nulls, undefined */
const safeNum = (val) => {
  if (val === null || val === undefined || val === '') return 0;
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return isNaN(n) ? 0 : n;
};

/** Get achievement percentage */
const achievementPct = (achieved, target) => {
  const t = safeNum(target);
  const a = safeNum(achieved);
  if (t === 0) return a > 0 ? 100 : 0;
  return Math.round((a / t) * 100);
};

/** Row color class based on achievement percentage */
const achievementRowClass = (pct) => {
  if (pct >= 66) return 'border-l-2 border-l-emerald-500/40';
  if (pct >= 33) return 'border-l-2 border-l-amber-500/40';
  if (pct > 0) return 'border-l-2 border-l-cyan-500/40';
  return 'border-l-2 border-l-slate-700/40';
};

/** Achievement badge */
const AchievementBadge = ({ pct }) => {
  let className = 'text-xs font-bold px-2 py-0.5 rounded ';
  if (pct >= 66) className += 'bg-emerald-500/10 text-emerald-400';
  else if (pct >= 33) className += 'bg-amber-500/10 text-amber-400';
  else if (pct > 0) className += 'bg-cyan-500/10 text-cyan-400';
  else className += 'bg-slate-700/50 text-slate-500';
  return <span className={className}>{pct}%</span>;
};

/** Get today's date in IST (YYYY-MM-DD) to match backend which uses Asia/Kolkata */
const getISTDate = () => {
  const now = new Date();
  const istMs = now.getTime() + (5.5 * 60 * 60 * 1000); // UTC + 5:30
  return new Date(istMs).toISOString().split('T')[0];
};

const isToday = (dateStr) => {
  if (!dateStr) return false;
  return dateStr.startsWith(getISTDate());
};

/** Normalize phone number for cross-matching IVR <-> Agent data */
const normalizePhone = (phone) => {
  if (!phone) return '';
  let p = String(phone).trim().replace(/[+\s-]/g, '');
  if (p.startsWith('91') && p.length > 10) p = p.slice(2);
  if (p.startsWith('0') && p.length > 10) p = p.slice(1);
  return p.length >= 10 ? p.slice(-10) : p;
};

/** Format seconds into Xm Ys */
const formatDuration = (seconds) => {
  const s = safeNum(seconds);
  if (s <= 0) return '0s';
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

// ─── SORT ICON ────────────────────────────────────────────────────────────────

const SortIcon = ({ column, sortConfig }) => {
  if (sortConfig.key !== column) {
    return <ChevronUp className="w-3 h-3 text-slate-600 opacity-40" />;
  }
  return sortConfig.direction === 'asc'
    ? <ChevronUp className="w-3 h-3 text-amber-400" />
    : <ChevronDown className="w-3 h-3 text-amber-400" />;
};

// ─── KPI CARD ─────────────────────────────────────────────────────────────────

const KpiCard = ({ label, value, sub, icon: Icon, color = 'text-white', valueColor, pulse }) => (
  <div className="flex items-center gap-3 bg-slate-800/60 border border-slate-700/50 rounded-lg px-4 py-3 min-w-[160px]">
    {Icon && (
      <div className={`p-2 rounded-md bg-slate-900/80 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
    )}
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium truncate">{label}</div>
      <div className={`text-xl font-bold tabular-nums ${valueColor || 'text-white'} ${pulse ? 'animate-pulse' : ''}`}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
    </div>
  </div>
);

// ─── CUSTOM RECHARTS TOOLTIP ──────────────────────────────────────────────────

const HourlyChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs shadow-lg">
      <div className="text-slate-300 font-medium mb-1">{label}</div>
      {payload.map((entry, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-slate-400">{entry.name}:</span>
          <span className="text-white font-mono">{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function InsideSalesIntelligence() {
  // ── User role info ──
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = storedUser.role || 'admin';
  const navigate = useNavigate();

  // ── MAIN TAB & FILTER STATE ──
  const [activeMainTab, setActiveMainTab] = useState('agents');  // agents|hourly|gyms|funnel
  const [selectedDate, setSelectedDate] = useState(getISTDate());
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [selectedGym, setSelectedGym] = useState('all');

  // ── NEW DATA STATE ──
  const [funnelData, setFunnelData] = useState(null);
  const [gymBreakdown, setGymBreakdown] = useState([]);
  const [staleLeads, setStaleLeads] = useState(null);
  const [leadStats, setLeadStats] = useState(null);
  const [gymSort, setGymSort] = useState({ key: 'visits_scheduled', direction: 'desc' });
  const [gymVisitStats, setGymVisitStats] = useState(null); // from rewritten gym-breakdown
  const [hotLeads, setHotLeads] = useState([]); // from rewritten stale-leads (individual records)
  const [funnelStages, setFunnelStages] = useState(null); // from new /inside-sales/funnel
  const [expandedGym, setExpandedGym] = useState(null);
  const [expandedHotLead, setExpandedHotLead] = useState(null);
  const [hideInactiveGyms, setHideInactiveGyms] = useState(true);

  // ── DATA FETCHING ──

  // Inside sales agents from WTF cache
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(true);

  useEffect(() => {
    const fetchAgents = () => {
      axiosInstance.get('/wtf-data/sales-agent-directory')
        .then(r => {
          const raw = r.data?.data || [];
          const seen = new Set();
          const unique = raw.filter(a => {
            const key = a.mobile || a.user_id || a.email;
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          setAgents(unique);
        })
        .catch(() => {})
        .finally(() => setAgentsLoading(false));
    };
    fetchAgents();
  }, []);

  // Per-agent call minutes (300-min target)
  const [agentCallMinutes, setAgentCallMinutes] = useState({});
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [dataStale, setDataStale] = useState(false);

  useEffect(() => {
    const fetchCallMin = () => {
      axiosInstance.get('/wtf-data/sales-agent-call-minutes')
        .then(r => {
          setAgentCallMinutes(r.data?.data || {});
          setLastRefreshTime(new Date());
          setDataStale(false);
        })
        .catch(() => setDataStale(true));
    };
    fetchCallMin();
    const timer = setInterval(fetchCallMin, 300000);
    return () => clearInterval(timer);
  }, []);

  // IVR data for recordings
  const [ivrData, setIvrData] = useState({ callLogs: [], c2cLogs: [] });
  const [ivrLoading, setIvrLoading] = useState(true);

  // Visit attribution from WTF lead cross-reference
  const [visitAttribution, setVisitAttribution] = useState({});

  useEffect(() => {
    const fetchIvr = async () => {
      try {
        const [callRes, c2cRes] = await Promise.all([
          axiosInstance.get('/ivr/call_logs'),
          axiosInstance.get('/ivr/c2c_logs'),
        ]);
        setIvrData({
          callLogs: callRes.data?.data || [],
          c2cLogs: c2cRes.data?.data || [],
        });
        setLastRefreshTime(new Date());
        setDataStale(false);
      } catch (e) {
        console.error('IVR fetch failed:', e);
        setDataStale(true);
      } finally {
        setIvrLoading(false);
      }
    };
    const fetchVisits = () => {
      axiosInstance.get(`/inside-sales/visit-attribution?date=${selectedDate}`)
        .then(r => setVisitAttribution(r.data?.data || {}))
        .catch(() => {});
    };
    fetchIvr();
    fetchVisits();
    const timer = setInterval(() => { fetchIvr(); fetchVisits(); }, 300000);
    return () => clearInterval(timer);
  }, [selectedDate]);

  // ── STALE DATA DETECTION ──
  const [refreshAgo, setRefreshAgo] = useState('');

  useEffect(() => {
    const tick = () => {
      if (!lastRefreshTime) { setRefreshAgo(''); return; }
      const diffSec = Math.round((Date.now() - lastRefreshTime.getTime()) / 1000);
      if (diffSec < 60) setRefreshAgo(`${diffSec}s ago`);
      else if (diffSec < 3600) setRefreshAgo(`${Math.floor(diffSec / 60)}m ago`);
      else setRefreshAgo(`${Math.floor(diffSec / 3600)}h ago`);
      if (diffSec > 360) setDataStale(true); // >6 min = stale
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [lastRefreshTime]);

  // ── TAB & LEADERBOARD STATE (must be before useEffect that references them) ──
  const [activeTab, setActiveTab] = useState('table'); // 'table' | 'leaderboard'
  const [leaderboardWindow, setLeaderboardWindow] = useState('today');
  const [leaderboardData, setLeaderboardData] = useState({ top: [], bottom: [], total_agents: 0, team_avg_minutes: 0 });
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);

  // Fetch leaderboard data when tab or window changes
  useEffect(() => {
    if (activeMainTab === 'agents' && activeTab === 'leaderboard') {
      // proceed to fetch
    } else {
      return;
    }
    setLeaderboardLoading(true);
    axiosInstance.get(`/agent-performance/leaderboard?window=${leaderboardWindow}`)
      .then(r => setLeaderboardData(r.data?.data || { top: [], bottom: [], total_agents: 0, team_avg_minutes: 0 }))
      .catch(() => toast.error('Failed to load leaderboard'))
      .finally(() => setLeaderboardLoading(false));
  }, [activeMainTab, activeTab, leaderboardWindow]);

  // ── NEW TAB DATA FETCHERS ──

  const fetchFunnelData = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/sales-funnel/v2?date=${selectedDate}`);
      setFunnelData(res.data?.data || null);
    } catch (e) {
      console.error('Funnel fetch failed:', e);
    }
  }, [selectedDate]);

  const fetchGymBreakdown = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/inside-sales/gym-breakdown?date=${selectedDate}`);
      setGymBreakdown(res.data?.data || []);
    } catch (e) {
      console.error('Gym breakdown fetch failed:', e);
    }
  }, [selectedDate]);

  const fetchStaleLeads = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/inside-sales/stale-leads?date=${selectedDate}`);
      setStaleLeads(res.data?.data || null);
    } catch (e) {
      console.error('Stale leads fetch failed:', e);
    }
  }, [selectedDate]);

  const fetchLeadStats = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/wtf/lead_stats?date=${selectedDate}`);
      setLeadStats(res.data?.data || res.data || null);
    } catch (e) {
      console.error('Lead stats fetch failed:', e);
    }
  }, [selectedDate]);

  const fetchGymVisitStats = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/inside-sales/gym-breakdown?date=${selectedDate}`);
      setGymVisitStats(res.data || null);
    } catch (e) { console.error('Gym visit stats failed:', e); }
  }, [selectedDate]);

  const fetchHotLeads = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/inside-sales/stale-leads?date=${selectedDate}`);
      setHotLeads(res.data?.data || []);
    } catch (e) { console.error('Hot leads failed:', e); }
  }, [selectedDate]);

  const fetchFunnelStages = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/inside-sales/funnel?date=${selectedDate}`);
      setFunnelStages(res.data || null);
    } catch (e) { console.error('Funnel failed:', e); }
  }, [selectedDate]);

  // ── LAZY LOADING: only fetch tab data when that tab is active ──
  useEffect(() => {
    if (activeMainTab === 'hourly' || activeMainTab === 'funnel') fetchFunnelData();
    if (activeMainTab === 'gyms' || activeMainTab === 'funnel') fetchGymBreakdown();
    if (activeMainTab === 'funnel') { fetchStaleLeads(); fetchLeadStats(); }
  }, [activeMainTab, selectedDate, fetchFunnelData, fetchGymBreakdown, fetchStaleLeads, fetchLeadStats]);

  useEffect(() => {
    if (activeMainTab === 'hourly') fetchGymVisitStats(); // for hourly visit data
    if (activeMainTab === 'gyms') fetchGymVisitStats();
    if (activeMainTab === 'funnel') fetchFunnelStages();
    if (activeMainTab === 'hotleads') fetchHotLeads();
  }, [activeMainTab, selectedDate, fetchGymVisitStats, fetchHotLeads, fetchFunnelStages]);

  // ── DERIVED DATA — Per-agent IVR lookup ──

  const agentIvrLookup = useMemo(() => {
    const lookup = {};

    // Build mobile-to-name map from agents
    const mobileToName = {};
    agents.forEach(agent => {
      const norm = normalizePhone(agent.mobile || agent.phone || '');
      if (norm) {
        mobileToName[norm] = agent.name || 'Unknown';
      }
    });

    // Process call_logs (attended_by field)
    ivrData.callLogs.forEach(log => {
      const agentPhone = normalizePhone(log.attended_by);
      if (!agentPhone || !mobileToName[agentPhone]) return;

      if (!lookup[agentPhone]) {
        lookup[agentPhone] = {
          totalCalls: 0,
          recordings: [],
          connectedDuration: 0,
          callsWithDuration: 0,
          c2cCalls: 0,
          incCalls: 0,
          uniqueClients: new Set(),
          uniqueConnected: new Set(),
        };
      }

      lookup[agentPhone].totalCalls++;
      lookup[agentPhone].incCalls++;

      const clientNo = normalizePhone(log.client_no || log.calling_no);
      if (clientNo) lookup[agentPhone].uniqueClients.add(clientNo);

      const disposition = String(log.disposition || log.call_disposition || '').toLowerCase();
      const isConnected = disposition.includes('connected');
      const duration = safeNum(log.call_duration || log.talk_time);

      if (isConnected && clientNo) {
        lookup[agentPhone].uniqueConnected.add(clientNo);
      }

      if (duration > 0) {
        lookup[agentPhone].connectedDuration += duration;
        lookup[agentPhone].callsWithDuration++;
      }

      lookup[agentPhone].recordings.push({
        clientNo: log.client_no || log.calling_no,
        direction: 'Inbound',
        disposition: isConnected ? 'connected' : 'missed',
        time: log.call_time || log.start_time,
        duration,
        url: log.recording_url,
        type: 'inbound',
      });
    });

    // Process c2c_logs (agent_no field)
    ivrData.c2cLogs.forEach(log => {
      const agentPhone = normalizePhone(log.agent_no);
      if (!agentPhone || !mobileToName[agentPhone]) return;

      if (!lookup[agentPhone]) {
        lookup[agentPhone] = {
          totalCalls: 0,
          recordings: [],
          connectedDuration: 0,
          callsWithDuration: 0,
          c2cCalls: 0,
          incCalls: 0,
          uniqueClients: new Set(),
          uniqueConnected: new Set(),
        };
      }

      lookup[agentPhone].totalCalls++;
      lookup[agentPhone].c2cCalls++;

      const clientNo = normalizePhone(log.client_no);
      if (clientNo) lookup[agentPhone].uniqueClients.add(clientNo);

      const disposition = String(log.disposition || log.call_disposition || '').toLowerCase();
      const isConnected = disposition === 'answered';
      const duration = safeNum(log.call_duration || log.talk_time);

      if (isConnected && clientNo) {
        lookup[agentPhone].uniqueConnected.add(clientNo);
      }

      if (duration > 0) {
        lookup[agentPhone].connectedDuration += duration;
        lookup[agentPhone].callsWithDuration++;
      }

      lookup[agentPhone].recordings.push({
        clientNo: log.client_no,
        direction: 'Outbound',
        disposition: isConnected ? 'connected' : 'missed',
        time: log.call_time || log.start_time,
        duration,
        url: log.recording_url,
        type: 'c2c',
      });
    });

    // Convert Sets to counts and sort recordings by time desc
    Object.keys(lookup).forEach(phone => {
      lookup[phone].uniqueCustomers = lookup[phone].uniqueClients.size;
      lookup[phone].uniqueConnected = lookup[phone].uniqueConnected.size;
      delete lookup[phone].uniqueClients;
      lookup[phone].recordings.sort((a, b) => {
        const tA = a.time ? new Date(a.time).getTime() : 0;
        const tB = b.time ? new Date(b.time).getTime() : 0;
        return tB - tA;
      });
    });

    return lookup;
  }, [agents, ivrData]);

  // ── AGENT TABLE ROWS ──

  const agentRows = useMemo(() => {
    return agents.map(agent => {
      const phone = normalizePhone(agent.mobile || agent.phone || '');
      const callData = agentCallMinutes[phone] || {};
      const ivrStats = agentIvrLookup[phone] || {
        totalCalls: 0,
        recordings: [],
        connectedDuration: 0,
        callsWithDuration: 0,
        c2cCalls: 0,
        incCalls: 0,
        uniqueCustomers: 0,
        uniqueConnected: 0
      };
      const visitData = visitAttribution[phone] || {};

      const totalMin = safeNum(callData.total_minutes);
      const targetPct = totalMin > 0 ? Math.round((totalMin / 300) * 100) : 0;
      const avgTalkSec = ivrStats.callsWithDuration > 0
        ? Math.round(ivrStats.connectedDuration / ivrStats.callsWithDuration)
        : 0;

      return {
        name: agent.name || 'Unknown',
        mobile: phone || '--',
        feature_group: agent.feature_group_name || '--',
        email: agent.email || '',
        joining_date: agent.joining_date || '',
        call_minutes: totalMin,
        target_pct: targetPct,
        avg_talk_sec: avgTalkSec,
        total_calls: safeNum(callData.total_calls),
        c2c_calls: safeNum(callData.c2c_calls),
        incoming_calls: safeNum(callData.incoming_calls),
        connected_calls: safeNum(callData.connected_calls),
        unique_customers: safeNum(callData.unique_customers),
        unique_connected: safeNum(callData.unique_connected),
        visit_scheduled: safeNum(visitData.visit_scheduled),
        visit_done: safeNum(visitData.visit_done),
        closures: safeNum(visitData.closure_expected),
        closure_revenue: null,   // Revenue attribution TBD
        recordings: ivrStats.recordings || [],
        achievement_pct: targetPct,
        remaining_minutes: safeNum(callData.remaining_minutes),
      };
    });
  }, [agents, agentCallMinutes, agentIvrLookup, visitAttribution]);

  // ── VIEW STATE ──

  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'call_minutes', direction: 'desc' });

  // ── SEARCH & SORT ──

  const filteredRows = useMemo(() => {
    let filtered = agentRows;

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(row =>
        row.name.toLowerCase().includes(term) ||
        row.mobile.includes(term)
      );
    }

    // Apply agent filter
    if (selectedAgent !== 'all') {
      filtered = filtered.filter(row => row.mobile === selectedAgent);
    }

    return filtered;
  }, [agentRows, searchTerm, selectedAgent]);

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];
    sorted.sort((a, b) => {
      const aVal = a[sortConfig.key] ?? 0;
      const bVal = b[sortConfig.key] ?? 0;

      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortConfig.direction === 'asc'
        ? aVal - bVal
        : bVal - aVal;
    });
    return sorted;
  }, [filteredRows, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const handleGymSort = (key) => {
    setGymSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // ── KPI AGGREGATIONS ──

  const kpis = useMemo(() => {
    // IVR-based metrics (correct for call tracking)
    const totalCallMinutes = agentRows.reduce((sum, row) => sum + row.call_minutes, 0);
    const totalCalls = agentRows.reduce((sum, row) => sum + row.total_calls, 0);

    // Weighted average of avg_talk_sec (IVR)
    const totalDuration = agentRows.reduce((sum, row) => {
      const mins = agentIvrLookup[row.mobile];
      return sum + (mins?.connectedDuration || 0);
    }, 0);
    const totalCallsWithDuration = agentRows.reduce((sum, row) => {
      const mins = agentIvrLookup[row.mobile];
      return sum + (mins?.callsWithDuration || 0);
    }, 0);
    const avgTalkSec = totalCallsWithDuration > 0
      ? Math.round(totalDuration / totalCallsWithDuration)
      : 0;

    // WTF CRM data for visits and closures (prefer funnel/WTF data over IVR-attributed)
    const agentVisitScheduled = agentRows.reduce((sum, row) => sum + row.visit_scheduled, 0);
    const agentVisitDone = agentRows.reduce((sum, row) => sum + row.visit_done, 0);
    const agentClosures = agentRows.reduce((sum, row) => sum + row.closures, 0);

    // Visits from golden endpoint summary
    const totalVisitScheduled = gymVisitStats?.summary?.total_scheduled || agentVisitScheduled;
    const totalVisitDone = gymVisitStats?.summary?.total_visit_done || agentVisitDone;
    const totalClosures = gymVisitStats?.summary?.total_converted || agentClosures;
    const staleCount = gymVisitStats?.summary?.total_visit_done_not_converted || 0;

    // Conv rate: closures / visits_done * 100
    const convRate = totalVisitDone > 0 ? Math.round((totalClosures / totalVisitDone) * 100) : 0;

    return {
      totalCallMinutes: Math.round(totalCallMinutes),
      agentCount: agents.length,
      avgTalkTime: avgTalkSec,
      totalCalls,
      totalVisitScheduled,
      totalVisitDone,
      totalClosures,
      revenueTarget: agents.length * 10000,
      convRate,
      staleCount,
    };
  }, [agentRows, agents.length, agentIvrLookup, staleLeads, funnelData, leadStats, gymVisitStats]);

  // ── DERIVED: Agent list for dropdown ──
  const agentList = useMemo(() => {
    return agentRows.map(r => ({ name: r.name, mobile: r.mobile })).sort((a, b) => a.name.localeCompare(b.name));
  }, [agentRows]);

  // ── DERIVED: Gym list for dropdown (from gymBreakdown) ──
  const gymList = useMemo(() => {
    return gymBreakdown.map(g => g.gym_name || g.name || 'Unknown').filter(Boolean).sort();
  }, [gymBreakdown]);

  // ── DERIVED: Sorted gym breakdown (prefer gymVisitStats golden endpoint, fall back to old gymBreakdown) ──
  const sortedGymBreakdown = useMemo(() => {
    let data = [...(gymVisitStats?.data || gymBreakdown)];
    if (selectedGym !== 'all') {
      data = data.filter(g => (g.gym_name || g.name) === selectedGym);
    }
    if (hideInactiveGyms) {
      data = data.filter(g => safeNum(g.scheduled || g.visits_scheduled) > 0 || safeNum(g.visit_done || g.visits_done) > 0 || safeNum(g.all_leads) > 0);
    }
    // Sort inactive gyms (0 visits + 0 leads) to bottom, then apply user sort
    data.sort((a, b) => {
      const aActive = safeNum(a.scheduled || a.visits_scheduled) + safeNum(a.visit_done || a.visits_done) + safeNum(a.all_leads);
      const bActive = safeNum(b.scheduled || b.visits_scheduled) + safeNum(b.visit_done || b.visits_done) + safeNum(b.all_leads);
      if (aActive === 0 && bActive > 0) return 1;
      if (bActive === 0 && aActive > 0) return -1;
      const aVal = safeNum(a[gymSort.key]);
      const bVal = safeNum(b[gymSort.key]);
      return gymSort.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return data;
  }, [gymBreakdown, gymVisitStats, gymSort, selectedGym, hideInactiveGyms]);

  // ── DERIVED: Hourly chart data from IVR call logs (same source as heatmap) ──
  const hourlyChartData = useMemo(() => {
    const hours = [10, 11, 12, 13, 14, 15, 16, 17, 18];
    const buckets = {};
    hours.forEach(h => { buckets[h] = { total: 0, connected: 0 }; });

    const allLogs = [...(ivrData?.callLogs || []), ...(ivrData?.c2cLogs || [])];
    allLogs.forEach(log => {
      const timeStr = log.call_time || log.start_time || '';
      if (!timeStr) return;
      const dt = new Date(timeStr);
      if (isNaN(dt.getTime())) return;
      const istHour = (dt.getUTCHours() + 5 + Math.floor((dt.getUTCMinutes() + 30) / 60)) % 24;
      if (!buckets[istHour]) return;
      buckets[istHour].total += 1;
      const dur = Number(log.call_duration || log.duration || 0);
      const disp = (log.disposition || '').toLowerCase();
      if (dur > 0 || disp === 'answered' || disp === 'connected') {
        buckets[istHour].connected += 1;
      }
    });

    return hours.map(h => ({
      time: `${h}:00`,
      totalCalls: buckets[h].total,
      connectedCalls: buckets[h].connected,
    })).filter(d => d.totalCalls > 0 || d.connectedCalls > 0);
  }, [ivrData]);

  // ── DERIVED: Agent heatmap from IVR call logs ──
  const agentHeatmap = useMemo(() => {
    const hours = [10, 11, 12, 13, 14, 15, 16, 17, 18];
    const heatmap = {};

    // Build mobile-to-name map
    const mobileToName = {};
    agents.forEach(agent => {
      const norm = normalizePhone(agent.mobile || agent.phone || '');
      if (norm) mobileToName[norm] = agent.name || 'Unknown';
    });

    // Aggregate calls by agent + hour
    const allLogs = [...ivrData.callLogs, ...ivrData.c2cLogs];
    allLogs.forEach(log => {
      const agentPhone = normalizePhone(log.attended_by || log.agent_no);
      if (!agentPhone || !mobileToName[agentPhone]) return;

      const timeStr = log.call_time || log.start_time || '';
      if (!timeStr) return;

      const dt = new Date(timeStr);
      if (isNaN(dt.getTime())) return;

      // Convert to IST hour
      const istHour = (dt.getUTCHours() + 5 + Math.floor((dt.getUTCMinutes() + 30) / 60)) % 24;

      if (!hours.includes(istHour)) return;

      const name = mobileToName[agentPhone];
      if (!heatmap[name]) {
        heatmap[name] = {};
        hours.forEach(h => { heatmap[name][h] = 0; });
      }
      heatmap[name][istHour]++;
    });

    // Apply agent filter
    if (selectedAgent !== 'all') {
      const agentName = mobileToName[selectedAgent];
      if (agentName && heatmap[agentName]) {
        return { [agentName]: heatmap[agentName] };
      }
      return {};
    }

    return heatmap;
  }, [agents, ivrData, selectedAgent]);

  // ── REFRESH HANDLER ──

  const handleRefresh = async () => {
    try {
      const [agentsRes, callMinRes, callRes, c2cRes, visitRes] = await Promise.all([
        axiosInstance.get('/wtf-data/sales-agent-directory'),
        axiosInstance.get('/wtf-data/sales-agent-call-minutes'),
        axiosInstance.get('/ivr/call_logs'),
        axiosInstance.get('/ivr/c2c_logs'),
        axiosInstance.get(`/inside-sales/visit-attribution?date=${selectedDate}`),
      ]);
      const rawAgents = agentsRes.data?.data || [];
      const seenAgents = new Set();
      const uniqueAgents = rawAgents.filter(a => {
        const key = a.mobile || a.user_id || a.email;
        if (!key || seenAgents.has(key)) return false;
        seenAgents.add(key);
        return true;
      });
      setAgents(uniqueAgents);
      setAgentCallMinutes(callMinRes.data?.data || {});
      setIvrData({
        callLogs: callRes.data?.data || [],
        c2cLogs: c2cRes.data?.data || [],
      });
      setVisitAttribution(visitRes.data?.data || {});
      setLastRefreshTime(new Date());
      setDataStale(false);
      toast.success('Data refreshed');
    } catch (error) {
      setDataStale(true);
      toast.error('Refresh failed');
    }
  };

  // ── LOADING STATE ──

  if (agentsLoading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-amber-500 mx-auto mb-2" />
          <div className="text-sm text-slate-400">Loading inside sales agents...</div>
        </div>
      </div>
    );
  }

  // ── RENDER ──

  const mainTabs = [
    { key: 'agents', label: 'Agent Performance', icon: Users },
    { key: 'hourly', label: 'Hourly Activity', icon: BarChart3 },
    { key: 'gyms', label: 'Gym Performance', icon: Building2 },
    { key: 'funnel', label: 'Lead Funnel', icon: Layers },
    { key: 'hotleads', label: 'Hot Leads', icon: AlertTriangle },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Card className="bg-slate-900 border-slate-700/50">
        <CardHeader className="bg-slate-900 border-b border-slate-700/50 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Headphones className="w-6 h-6 text-amber-500" />
              <div>
                <CardTitle className="text-2xl font-bold text-slate-100">
                  Inside Sales Intelligence
                </CardTitle>
                <div className="flex items-center gap-2 mt-1">
                  {dataStale ? (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-900/40 text-amber-400 text-[10px] font-semibold border border-amber-700/50">
                      <AlertTriangle className="h-3 w-3" />
                      STALE
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green-900/40 text-green-400 text-[10px] font-semibold border border-green-700/50">
                      <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse"></span>
                      LIVE
                    </span>
                  )}
                  <span className="text-xs text-slate-400">
                    {refreshAgo ? `Updated ${refreshAgo}` : 'Real-time agent performance tracking'}
                  </span>
                </div>
              </div>
            </div>
            <Button
              onClick={handleRefresh}
              variant="outline"
              size="sm"
              className="border-slate-600 hover:bg-slate-800"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-6">
          {/* KPI Header — 8 cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-4">
            <KpiCard
              label="Total Call Minutes"
              value={`${kpis.totalCallMinutes}m`}
              sub={`${kpis.agentCount} agents`}
              icon={Phone}
              color="text-cyan-400"
              valueColor="text-slate-100"
            />
            <KpiCard
              label="Avg Talk Time"
              value={kpis.avgTalkTime > 0 ? formatDuration(kpis.avgTalkTime) : '--'}
              sub="Per call avg"
              icon={Clock}
              color="text-blue-400"
              valueColor={kpis.avgTalkTime >= 600 ? 'text-emerald-400' : kpis.avgTalkTime > 0 ? 'text-slate-100' : 'text-slate-500'}
            />
            <KpiCard
              label="Total Calls Today"
              value={kpis.totalCalls}
              sub={`${Math.round(kpis.totalCalls / (kpis.agentCount || 1))} per agent`}
              icon={PhoneCall}
              color="text-purple-400"
              valueColor="text-slate-100"
            />
            <KpiCard
              label="Visits"
              value={`${kpis.totalVisitScheduled} / ${kpis.totalVisitDone}`}
              sub="Scheduled / Done"
              icon={Users}
              color="text-cyan-400"
              valueColor={kpis.totalVisitDone > 0 ? 'text-emerald-400' : 'text-slate-100'}
            />
            <KpiCard
              label="Closures Today"
              value={kpis.totalClosures || 0}
              sub={`From ${kpis.agentCount} agents`}
              icon={DollarSign}
              color={kpis.totalClosures > 0 ? 'text-emerald-400' : 'text-slate-500'}
              valueColor={kpis.totalClosures > 0 ? 'text-emerald-400' : 'text-slate-500'}
            />
            <KpiCard
              label="Conv Rate"
              value={`${kpis.convRate}%`}
              sub={`${kpis.totalClosures} / ${kpis.totalVisitDone} visits`}
              icon={TrendingUp}
              color={kpis.convRate >= 30 ? 'text-emerald-400' : 'text-amber-400'}
              valueColor={kpis.convRate >= 30 ? 'text-emerald-400' : 'text-amber-400'}
            />
            <KpiCard
              label="Stale Leads"
              value={kpis.staleCount}
              sub="Visited, not converted"
              icon={AlertTriangle}
              color={kpis.staleCount > 5 ? 'text-red-400' : kpis.staleCount > 0 ? 'text-amber-400' : 'text-slate-500'}
              valueColor={kpis.staleCount > 5 ? 'text-red-400' : kpis.staleCount > 0 ? 'text-amber-400' : 'text-slate-500'}
              pulse={kpis.staleCount > 5}
            />
            <KpiCard
              label="Sched to Done"
              value={kpis.totalVisitScheduled > 0 ? `${Math.round((kpis.totalVisitDone / kpis.totalVisitScheduled) * 100)}%` : '--'}
              sub={`${kpis.totalVisitDone} of ${kpis.totalVisitScheduled}`}
              icon={CheckCircle}
              color="text-blue-400"
              valueColor="text-slate-100"
            />
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-3 bg-slate-800/40 border border-slate-700/50 rounded-lg px-4 py-3">
            <Filter className="w-4 h-4 text-slate-400" />
            <div className="flex items-center gap-2">
              <label className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Date</label>
              <div className="flex items-center gap-1">
                <button onClick={() => {
                  const d = new Date(selectedDate);
                  d.setDate(d.getDate() - 1);
                  setSelectedDate(d.toISOString().split('T')[0]);
                }} className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-white">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="bg-slate-900 border border-slate-700 text-slate-100 rounded px-2 py-1 text-sm"
                />
                <button onClick={() => {
                  const d = new Date(selectedDate);
                  d.setDate(d.getDate() + 1);
                  const today = new Date().toISOString().split('T')[0];
                  const next = d.toISOString().split('T')[0];
                  if (next <= today) setSelectedDate(next);
                }} className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-white">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Agent</label>
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                className="bg-slate-900 border border-slate-700 rounded-md px-3 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-amber-500/50 focus:border-amber-500/50 min-w-[150px]"
              >
                <option value="all">All Agents</option>
                {agentList.map(a => (
                  <option key={a.mobile} value={a.mobile}>{a.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Gym</label>
              <select
                value={selectedGym}
                onChange={(e) => setSelectedGym(e.target.value)}
                className="bg-slate-900 border border-slate-700 rounded-md px-3 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-amber-500/50 focus:border-amber-500/50 min-w-[150px]"
              >
                <option value="all">All Gyms</option>
                {gymList.map(g => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
            {(selectedAgent !== 'all' || selectedGym !== 'all' || selectedDate !== getISTDate()) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setSelectedAgent('all'); setSelectedGym('all'); setSelectedDate(getISTDate()); }}
                className="text-slate-400 hover:text-white text-xs"
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Main Tab Navigation */}
          <div className="flex gap-1 bg-slate-800/60 rounded-lg p-1 overflow-x-auto">
            {mainTabs.map(tab => {
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveMainTab(tab.key)}
                  className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                    activeMainTab === tab.key
                      ? 'bg-amber-600 text-white'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <TabIcon className="w-4 h-4" />
                  {tab.label}
                  {tab.key === 'hotleads' && hotLeads.length > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500/20 text-red-400">
                      {hotLeads.length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* ═══════════════════════════════════════════════════════════════════════
              TAB 1: AGENT PERFORMANCE
              ═══════════════════════════════════════════════════════════════════════ */}
          {activeMainTab === 'agents' && (
            <div className="space-y-4">
              {/* Search Bar */}
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    placeholder="Search by agent name or mobile..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-slate-100"
                  />
                </div>
                <div className="text-sm text-slate-400">
                  {sortedRows.length} of {agentRows.length} agents
                </div>
              </div>

              {/* Sub-tab: Table vs Leaderboard */}
              <div className="flex gap-1 bg-slate-800/40 rounded-lg p-1 w-fit">
                <button
                  onClick={() => setActiveTab('table')}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'table'
                      ? 'bg-slate-700 text-white'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Agent Table
                </button>
                <button
                  onClick={() => setActiveTab('leaderboard')}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'leaderboard'
                      ? 'bg-slate-700 text-white'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Leaderboard
                </button>
              </div>

              {/* Agent Table */}
              {activeTab === 'table' && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-slate-800/60 sticky top-0 z-10">
                    <tr className="border-b border-slate-700">
                      <th className="px-3 py-3 text-left text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                        #
                      </th>
                      <th
                        className="px-3 py-3 text-left text-[10px] uppercase tracking-wider text-slate-400 font-bold cursor-pointer hover:text-amber-400"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center gap-1">
                          Agent Name
                          <SortIcon column="name" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th className="px-3 py-3 text-left text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                        Mobile
                      </th>
                      <th className="px-3 py-3 text-left text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                        Feature Group
                      </th>
                      <th
                        className="px-3 py-3 text-left text-[10px] uppercase tracking-wider text-slate-400 font-bold cursor-pointer hover:text-amber-400"
                        onClick={() => handleSort('call_minutes')}
                      >
                        <div className="flex items-center gap-1">
                          Call Minutes
                          <SortIcon column="call_minutes" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th
                        className="px-3 py-3 text-center text-[10px] uppercase tracking-wider text-slate-400 font-bold cursor-pointer hover:text-amber-400"
                        onClick={() => handleSort('avg_talk_sec')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Avg/Call
                          <SortIcon column="avg_talk_sec" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th
                        className="px-3 py-3 text-center text-[10px] uppercase tracking-wider text-slate-400 font-bold cursor-pointer hover:text-amber-400"
                        onClick={() => handleSort('total_calls')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Total Calls
                          <SortIcon column="total_calls" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th
                        className="px-3 py-3 text-center text-[10px] uppercase tracking-wider text-slate-400 font-bold cursor-pointer hover:text-amber-400"
                        onClick={() => handleSort('c2c_calls')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          C2C
                          <SortIcon column="c2c_calls" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th
                        className="px-3 py-3 text-center text-[10px] uppercase tracking-wider text-slate-400 font-bold cursor-pointer hover:text-amber-400"
                        onClick={() => handleSort('visit_scheduled')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          V.Sched
                          <SortIcon column="visit_scheduled" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th
                        className="px-3 py-3 text-center text-[10px] uppercase tracking-wider text-slate-400 font-bold cursor-pointer hover:text-amber-400"
                        onClick={() => handleSort('visit_done')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          V.Done
                          <SortIcon column="visit_done" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th
                        className="px-3 py-3 text-center text-[10px] uppercase tracking-wider text-slate-400 font-bold cursor-pointer hover:text-amber-400"
                        onClick={() => handleSort('closures')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Closures
                          <SortIcon column="closures" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th
                        className="px-3 py-3 text-center text-[10px] uppercase tracking-wider text-slate-400 font-bold cursor-pointer hover:text-amber-400"
                        onClick={() => handleSort('achievement_pct')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Achievement %
                          <SortIcon column="achievement_pct" sortConfig={sortConfig} />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.length === 0 ? (
                      <tr>
                        <td colSpan={12} className="px-3 py-8 text-center text-slate-500">
                          No agents found
                        </td>
                      </tr>
                    ) : (
                      sortedRows.map((row, idx) => {
                        const agentKey = `${row.mobile}-${idx}`;
                        const rowColorClass = achievementRowClass(row.achievement_pct);

                        return (
                          <React.Fragment key={agentKey}>
                            <tr
                              className={`border-b border-slate-800 hover:bg-slate-800/30 transition-colors cursor-pointer ${rowColorClass}`}
                              onClick={() => navigate(`/inside-sales/${row.mobile}`)}
                            >
                              {/* # */}
                              <td className="px-3 py-2 text-slate-500 font-mono text-xs">
                                {idx + 1}
                              </td>
                              {/* Agent Name */}
                              <td className="px-3 py-2 font-medium text-slate-100">
                                {row.name}
                              </td>
                              {/* Mobile */}
                              <td className="px-3 py-2 font-mono text-slate-400 text-xs">
                                {row.mobile}
                              </td>
                              {/* Feature Group */}
                              <td className="px-3 py-2 text-slate-400 text-xs truncate max-w-[150px]" title={row.feature_group}>
                                {row.feature_group}
                              </td>
                              {/* Call Minutes (progress bar) */}
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 bg-slate-800 rounded-full h-2 overflow-hidden min-w-[80px]">
                                    <div
                                      className={`h-full transition-all ${
                                        row.target_pct >= 66 ? 'bg-emerald-500' :
                                        row.target_pct >= 33 ? 'bg-amber-500' :
                                        row.target_pct > 0 ? 'bg-cyan-500' :
                                        'bg-slate-700'
                                      }`}
                                      style={{ width: `${Math.min(row.target_pct, 100)}%` }}
                                    />
                                  </div>
                                  <span className="text-xs font-mono text-slate-300 whitespace-nowrap min-w-[70px] text-right">
                                    {Math.round(row.call_minutes)}m / 300m
                                  </span>
                                </div>
                              </td>
                              {/* Avg/Call */}
                              <td className="px-3 py-2 text-center text-slate-300 font-mono text-xs">
                                {row.avg_talk_sec > 0 ? formatDuration(row.avg_talk_sec) : '--'}
                              </td>
                              {/* Total Calls */}
                              <td className="px-3 py-2 text-center font-mono text-slate-300">
                                {row.total_calls || 0}
                              </td>
                              {/* C2C */}
                              <td className="px-3 py-2 text-center font-mono text-slate-300">
                                {row.c2c_calls || 0}
                              </td>
                              {/* Visit Scheduled */}
                              <td className="px-3 py-2 text-center font-mono text-slate-300">
                                {row.visit_scheduled > 0 ? (
                                  <span className="text-cyan-400">{row.visit_scheduled}</span>
                                ) : (
                                  <span className="text-slate-600">0</span>
                                )}
                              </td>
                              {/* Visit Done */}
                              <td className="px-3 py-2 text-center font-mono text-slate-300">
                                {row.visit_done > 0 ? (
                                  <span className="text-emerald-400">{row.visit_done}</span>
                                ) : (
                                  <span className="text-slate-600">0</span>
                                )}
                              </td>
                              {/* Closures */}
                              <td className="px-3 py-2 text-center font-mono text-slate-300">
                                {row.closures > 0 ? (
                                  <span className="text-amber-400">{row.closures}</span>
                                ) : (
                                  <span className="text-slate-600">0</span>
                                )}
                              </td>
                              {/* Achievement % */}
                              <td className="px-3 py-2 text-center">
                                <AchievementBadge pct={row.achievement_pct} />
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              )}

              {/* Leaderboard Sub-tab */}
              {activeTab === 'leaderboard' && (
                <div className="space-y-4">
                  {/* Time window toggles */}
                  <div className="flex gap-2">
                    {['today', 'week', 'month'].map(w => (
                      <Button
                        key={w}
                        variant={leaderboardWindow === w ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setLeaderboardWindow(w)}
                        className={leaderboardWindow === w ? 'bg-amber-600 hover:bg-amber-700' : 'border-slate-600'}
                      >
                        {w === 'today' ? 'Today' : w === 'week' ? 'This Week' : 'This Month'}
                      </Button>
                    ))}
                  </div>

                  {leaderboardLoading ? (
                    <div className="text-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin text-amber-500 mx-auto" />
                    </div>
                  ) : (
                    <>
                      {/* Team Stats Bar */}
                      <div className="flex gap-4 text-sm text-slate-400">
                        <span>{leaderboardData.total_agents} agents</span>
                        <span>Team avg: {Math.round(leaderboardData.team_avg_minutes || 0)}m</span>
                      </div>

                      {/* Top 5 Performers */}
                      <Card className="bg-slate-900 border-slate-700/50">
                        <CardHeader className="pb-2">
                          <CardTitle className="flex items-center gap-2 text-lg">
                            <Award className="w-5 h-5 text-amber-400" />
                            Top Performers
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {leaderboardData.top.length === 0 ? (
                            <div className="text-center py-4 text-slate-500 text-sm">
                              {leaderboardWindow !== 'today' ? 'Insufficient historical data — available after daily snapshots accumulate' : 'No data available'}
                            </div>
                          ) : (
                            <div className="space-y-1">
                              {leaderboardData.top.map((agent, idx) => (
                                <div
                                  key={agent.agent_id || idx}
                                  className="flex items-center gap-3 py-3 px-2 border-b border-slate-800 last:border-0 hover:bg-slate-800/40 cursor-pointer rounded transition-colors"
                                  onClick={() => navigate(`/inside-sales/${agent.agent_id}`)}
                                >
                                  <div className={`text-xl font-bold w-8 text-center ${
                                    idx < 3 ? 'text-emerald-400' : 'text-slate-500'
                                  }`}>
                                    #{idx + 1}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-slate-200 truncate">{agent.name}</div>
                                    <div className="text-xs text-slate-500">
                                      {Math.round(agent.call_minutes || 0)}m
                                      {leaderboardWindow !== 'today' && agent.days_active ? ` (${agent.days_active} days)` : ''}
                                    </div>
                                  </div>
                                  <AchievementBadge pct={agent.target_pct || 0} />
                                </div>
                              ))}
                            </div>
                          )}
                          {leaderboardWindow !== 'today' && (
                            <div className="text-[10px] text-slate-600 mt-3 pt-2 border-t border-slate-800">
                              Historical data available after daily snapshots accumulate. "Unknown" agents appear when phone numbers haven't been mapped to names.
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Bottom 5 — Private (admin only, no names shown publicly) */}
                      <Card className="bg-slate-900 border-amber-900/40">
                        <CardHeader className="pb-2">
                          <CardTitle className="flex items-center gap-2 text-lg text-amber-400">
                            <AlertTriangle className="w-5 h-5" />
                            Needs Attention
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-slate-400 mb-3">
                            {leaderboardData.bottom.length} agent{leaderboardData.bottom.length !== 1 ? 's' : ''} below 50% target.
                            Review recommended.
                          </p>
                          {/* Show names only for admin role — per locked decision: bottom 5 flagged privately to managers only */}
                          {userRole === 'admin' && leaderboardData.bottom.length > 0 && (
                            <div className="space-y-1">
                              {leaderboardData.bottom.map((agent, idx) => (
                                <div
                                  key={agent.agent_id || idx}
                                  className="flex items-center justify-between py-2 px-2 border-b border-slate-800/50 last:border-0 hover:bg-slate-800/40 cursor-pointer rounded transition-colors"
                                  onClick={() => navigate(`/inside-sales/${agent.agent_id}`)}
                                >
                                  <span className="text-sm text-slate-400">{agent.name}</span>
                                  <span className="text-xs font-mono text-amber-400">{Math.round(agent.call_minutes || 0)}m ({agent.target_pct || 0}%)</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════════════
              TAB 2: HOURLY ACTIVITY
              ═══════════════════════════════════════════════════════════════════════ */}
          {activeMainTab === 'hourly' && (
            <div className="space-y-6">
              {/* Stacked Bar Chart */}
              <Card className="bg-slate-900 border-slate-700/50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
                    <BarChart3 className="w-5 h-5 text-cyan-400" />
                    Call Volume by Time Window
                  </CardTitle>
                  <p className="text-xs text-slate-500 font-normal mt-1">Outbound C2C calls aggregated by hour from IVR logs</p>
                </CardHeader>
                <CardContent>
                  {hourlyChartData.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <div className="text-sm">No hourly data available for {selectedDate}</div>
                      <div className="text-xs mt-1 text-slate-600">Data populates from the sales funnel API</div>
                    </div>
                  ) : (
                    <div className="h-[350px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={hourlyChartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis
                            dataKey="time"
                            tick={{ fill: '#94a3b8', fontSize: 11 }}
                            axisLine={{ stroke: '#475569' }}
                            tickLine={{ stroke: '#475569' }}
                          />
                          <YAxis
                            tick={{ fill: '#94a3b8', fontSize: 11 }}
                            axisLine={{ stroke: '#475569' }}
                            tickLine={{ stroke: '#475569' }}
                            label={{ value: 'Calls', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 12 }}
                          />
                          <Tooltip content={<HourlyChartTooltip />} />
                          <Legend
                            wrapperStyle={{ fontSize: 12, color: '#94a3b8' }}
                          />
                          <Bar
                            dataKey="totalCalls"
                            name="Total Calls"
                            fill="#475569"
                            radius={[0, 0, 0, 0]}
                          />
                          <Bar
                            dataKey="connectedCalls"
                            name="Connected"
                            fill="#10b981"
                            radius={[2, 2, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Visit Activity by Hour (from WTF CRM) */}
              <div className="bg-slate-900 rounded-xl border border-slate-700/50 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-4 h-4 text-cyan-400" />
                  <span className="text-sm font-medium text-slate-100">Visit Activity by Hour</span>
                  <span className="text-xs text-slate-500">From WTF CRM visit schedule data</span>
                </div>
                {gymVisitStats?.hourly_scheduled?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={gymVisitStats.hourly_scheduled.map((s, i) => ({
                      hour: `${s.hour}:00`,
                      scheduled: s.count,
                      done: (gymVisitStats.hourly_done || [])[i]?.count || 0,
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="hour" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                      <Tooltip content={<HourlyChartTooltip />} />
                      <Bar dataKey="scheduled" name="Scheduled" fill="#22d3ee" radius={[2,2,0,0]} />
                      <Bar dataKey="done" name="Done" fill="#10b981" radius={[2,2,0,0]} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-8 text-slate-500 text-sm">No visit hourly data for {selectedDate}</div>
                )}
              </div>

              {/* Agent Heatmap */}
              <Card className="bg-slate-900 border-slate-700/50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
                    <Activity className="w-5 h-5 text-amber-400" />
                    Agent Heatmap (Calls per Hour)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.keys(agentHeatmap).length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <div className="text-sm">No agent call data available to build heatmap</div>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="border-b border-slate-700">
                            <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-slate-400 font-bold">Agent</th>
                            {[10, 11, 12, 13, 14, 15, 16, 17, 18].map(h => (
                              <th key={h} className="px-2 py-2 text-center text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                                {h}:00
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(agentHeatmap).sort(([a], [b]) => a.localeCompare(b)).map(([name, hours]) => (
                            <tr key={name} className="border-b border-slate-800">
                              <td className="px-3 py-2 text-slate-200 text-xs font-medium whitespace-nowrap">{name}</td>
                              {[10, 11, 12, 13, 14, 15, 16, 17, 18].map(h => {
                                const count = hours[h] || 0;
                                let cellClass = 'bg-slate-800/30 text-slate-600';
                                if (count > 5) cellClass = 'bg-emerald-500/20 text-emerald-400';
                                else if (count >= 1) cellClass = 'bg-amber-500/20 text-amber-400';
                                return (
                                  <td key={h} className="px-2 py-2 text-center">
                                    <span
                                      className={`inline-block w-8 h-7 leading-7 rounded text-xs font-mono ${cellClass}`}
                                      title={`${count} outbound calls made by ${name} between ${h}:00\u2013${h}:59`}
                                    >
                                      {count}
                                    </span>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════════════
              TAB 3: GYM PERFORMANCE
              ═══════════════════════════════════════════════════════════════════════ */}
          {activeMainTab === 'gyms' && (() => {
            const filteredGyms = selectedAgent === 'all'
              ? sortedGymBreakdown
              : sortedGymBreakdown.filter(g =>
                  g.leads?.some(l => l.agent?.toLowerCase().includes(
                    (agentList.find(a => a.mobile === selectedAgent)?.name || selectedAgent).toLowerCase()
                  ))
                ).map(g => {
                  const agentName = (agentList.find(a => a.mobile === selectedAgent)?.name || selectedAgent).toLowerCase();
                  const agentLeads = g.leads?.filter(l => l.agent?.toLowerCase().includes(agentName)) || [];
                  return {
                    ...g,
                    leads: agentLeads,
                    scheduled: agentLeads.length || 0,
                    visit_done: agentLeads.filter(l => l.is_visit_done).length || 0,
                    converted: agentLeads.filter(l => l.is_converted).length || 0,
                    stale: agentLeads.filter(l => l.is_visit_done && !l.is_converted).length || 0,
                    pending: agentLeads.filter(l => !l.is_visit_done).length || 0,
                  };
                });
            return (
            <div className="space-y-4">
              <Card className="bg-slate-900 border-slate-700/50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
                    <Building2 className="w-5 h-5 text-cyan-400" />
                    Gym-wise Performance
                    <span className="text-xs text-slate-500 font-normal ml-2">
                      {filteredGyms.length} gym{filteredGyms.length !== 1 ? 's' : ''}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); setHideInactiveGyms(prev => !prev); }}
                      className="ml-auto text-[10px] text-slate-500 hover:text-slate-300 font-normal border border-slate-700 rounded px-2 py-0.5 transition-colors"
                    >
                      {hideInactiveGyms ? 'Show inactive gyms' : 'Hide inactive gyms'}
                    </button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {filteredGyms.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Building2 className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <div className="text-sm">No gym performance data for {selectedDate}</div>
                      <div className="text-xs mt-1 text-slate-600">Data populates from the gym breakdown API</div>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead className="bg-slate-800/60">
                          <tr className="border-b border-slate-700">
                            {[
                              { key: 'gym_name', label: 'Gym Name', align: 'left' },
                              { key: 'scheduled', label: 'Scheduled', align: 'center' },
                              { key: 'visit_done', label: 'V.Done', align: 'center' },
                              { key: 'pending', label: 'Pending', align: 'center' },
                              { key: 'converted', label: 'Converted', align: 'center' },
                              { key: 'stale', label: 'Stale', align: 'center' },
                              { key: 'conversion_rate', label: 'Conv%', align: 'center' },
                              { key: 'sched_to_done_rate', label: 'Sched\u2192Done%', align: 'center' },
                            ].map(col => (
                              <th
                                key={col.key}
                                className={`px-3 py-3 text-${col.align} text-[10px] uppercase tracking-wider text-slate-400 font-bold cursor-pointer hover:text-amber-400`}
                                onClick={() => handleGymSort(col.key)}
                              >
                                <div className={`flex items-center ${col.align === 'center' ? 'justify-center' : ''} gap-1`}>
                                  {col.label}
                                  <SortIcon column={col.key} sortConfig={gymSort} />
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredGyms.map((gym, idx) => {
                            const gymName = gym.gym_name || gym.name || 'Unknown';
                            const vSched = safeNum(gym.scheduled || gym.visits_scheduled);
                            const vDone = safeNum(gym.visit_done || gym.visits_done);
                            const pending = safeNum(gym.pending);
                            const converted = safeNum(gym.converted || gym.closures);
                            const stale = safeNum(gym.stale);
                            const convPct = safeNum(gym.conversion_rate) || (vDone > 0 ? Math.round((converted / vDone) * 100) : 0);
                            const schedDonePct = safeNum(gym.sched_to_done_rate) || (vSched > 0 ? Math.round((vDone / vSched) * 100) : 0);

                            let convColor = 'text-red-400';
                            if (convPct >= 30) convColor = 'text-emerald-400';
                            else if (convPct >= 15) convColor = 'text-amber-400';

                            let schedDoneColor = 'text-red-400';
                            if (schedDonePct >= 70) schedDoneColor = 'text-emerald-400';
                            else if (schedDonePct >= 40) schedDoneColor = 'text-amber-400';

                            const displaySchedDonePct = Math.min(schedDonePct, 100);

                            const isExpanded = expandedGym === gymName;

                            return (
                              <React.Fragment key={gymName + idx}>
                                <tr
                                  className="border-b border-slate-800 hover:bg-slate-800/30 transition-colors cursor-pointer"
                                  onClick={() => setExpandedGym(isExpanded ? null : gymName)}
                                >
                                  <td className="px-3 py-2 text-slate-100 font-medium">
                                    <div className="flex items-center gap-2">
                                      <ChevronDown className={`w-3 h-3 text-slate-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                      {gymName}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-center font-mono text-cyan-400">{vSched}</td>
                                  <td className="px-3 py-2 text-center font-mono text-emerald-400">{vDone}</td>
                                  <td className="px-3 py-2 text-center font-mono text-slate-300">{pending}</td>
                                  <td className="px-3 py-2 text-center font-mono text-amber-400">{converted}</td>
                                  <td className="px-3 py-2 text-center font-mono text-red-400">{stale}</td>
                                  <td className={`px-3 py-2 text-center font-mono font-bold ${convColor}`}>{convPct}%</td>
                                  <td className={`px-3 py-2 text-center font-mono font-bold ${schedDoneColor}`}>
                                    {displaySchedDonePct}%
                                    {schedDonePct > 100 && <span className="text-[9px] text-slate-500 font-normal ml-1" title="Raw value exceeds 100% due to carry-over visits">*</span>}
                                  </td>
                                </tr>
                                {/* Expanded: Individual leads for this gym */}
                                {isExpanded && gym.leads?.length > 0 && (
                                  <tr>
                                    <td colSpan={9} className="bg-slate-800/30 px-4 py-3">
                                      <div className="text-xs font-medium text-slate-400 mb-2">Individual Leads ({gym.leads.length})</div>
                                      <table className="w-full text-xs">
                                        <thead>
                                          <tr className="text-slate-500">
                                            <th className="text-left py-1">Name</th>
                                            <th className="text-left">Phone</th>
                                            <th className="text-left">Agent</th>
                                            <th className="text-center">Visit Done</th>
                                            <th className="text-center">Converted</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {gym.leads.map((lead, li) => (
                                            <tr key={li} className="border-t border-slate-700/30">
                                              <td className="py-1.5 text-slate-200">{lead.name}</td>
                                              <td className="text-slate-400 font-mono">{lead.mobile}</td>
                                              <td className="text-slate-300">{lead.agent}</td>
                                              <td className="text-center">
                                                {lead.is_visit_done
                                                  ? <span className="text-emerald-400">Yes</span>
                                                  : <span className="text-slate-500">No</span>}
                                              </td>
                                              <td className="text-center">
                                                {lead.is_converted
                                                  ? <span className="text-emerald-400">Converted</span>
                                                  : lead.is_visit_done
                                                    ? <span className="text-red-400">NOT CONVERTED</span>
                                                    : <span className="text-slate-500">--</span>}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </td>
                                  </tr>
                                )}
                                {/* Expanded: Agents working this gym (fallback if no leads array) */}
                                {isExpanded && (!gym.leads || gym.leads.length === 0) && (
                                  <tr>
                                    <td colSpan={9} className="bg-slate-800/30 px-6 py-3">
                                      <div className="text-xs text-slate-400 mb-2 font-medium">Agents working {gymName}:</div>
                                      {gym.agents && gym.agents.length > 0 ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                          {gym.agents.map((agent, aIdx) => (
                                            <div key={aIdx} className="flex items-center gap-2 bg-slate-900/60 rounded px-3 py-1.5 border border-slate-700/30">
                                              <span className="text-slate-200 text-xs">{agent.name || agent.agent_name || 'Agent'}</span>
                                              {agent.visits_done !== undefined && (
                                                <span className="text-[10px] text-emerald-400 font-mono ml-auto">{agent.visits_done} done</span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <div className="text-xs text-slate-600">No lead or agent data available for this gym</div>
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                          {/* Totals Row */}
                          <tr className="bg-slate-800/80 font-bold border-t-2 border-slate-600 sticky bottom-0">
                            <td className="px-3 py-2 text-slate-100">TOTAL ({filteredGyms.length} gyms)</td>
                            <td className="px-3 py-2 text-center text-cyan-400">{filteredGyms.reduce((s,g) => s + safeNum(g.scheduled || g.visits_scheduled), 0)}</td>
                            <td className="px-3 py-2 text-center text-emerald-400">{filteredGyms.reduce((s,g) => s + safeNum(g.visit_done || g.visits_done), 0)}</td>
                            <td className="px-3 py-2 text-center text-slate-400">{filteredGyms.reduce((s,g) => s + safeNum(g.pending), 0)}</td>
                            <td className="px-3 py-2 text-center text-emerald-400">{filteredGyms.reduce((s,g) => s + safeNum(g.converted || g.closures), 0)}</td>
                            <td className="px-3 py-2 text-center text-red-400">{filteredGyms.reduce((s,g) => s + safeNum(g.stale), 0)}</td>
                            <td className="px-3 py-2 text-center">--</td>
                            <td className="px-3 py-2 text-center">--</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            );
          })()}

          {/* ═══════════════════════════════════════════════════════════════════════
              TAB 4: LEAD FUNNEL
              ═══════════════════════════════════════════════════════════════════════ */}
          {activeMainTab === 'funnel' && (
            <div className="space-y-4">
              <Card className="bg-slate-900 border-slate-700/50">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-blue-400" />
                    <span>Sales Funnel</span>
                    <span className="text-xs text-slate-500">{selectedDate}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  {funnelStages ? (() => {
                    const kpis_f = funnelStages.lead_kpis || {};
                    const summary = funnelStages.visit_summary || {};
                    const stages = [
                      { label: "Today's Leads", value: safeNum(kpis_f.todays_leads), color: 'bg-blue-500' },
                      { label: "Follow-ups Today", value: safeNum(kpis_f.todays_follow_ups), color: 'bg-cyan-500' },
                      { label: "Visit Scheduled", value: safeNum(summary.total_scheduled), color: 'bg-amber-500' },
                      { label: "Visit Done", value: safeNum(summary.total_visit_done), color: 'bg-emerald-500' },
                      { label: "Converted", value: safeNum(summary.total_converted), color: 'bg-green-500' },
                    ];
                    const maxVal = Math.max(...stages.map(s => s.value), 1);
                    return (
                      <div className="space-y-3">
                        {stages.map((stage, i) => {
                          const prev = i > 0 ? stages[i - 1].value : 0;
                          const pct = prev > 0 ? Math.round((stage.value / prev) * 100) : '--';
                          return (
                            <div key={stage.label} className="flex items-center gap-3">
                              <div className="w-36 text-right text-xs text-slate-400">{stage.label}</div>
                              <div className="flex-1 relative h-8 bg-slate-800 rounded overflow-hidden">
                                <div className={`h-full ${stage.color} rounded flex items-center pl-3`}
                                  style={{ width: `${Math.max((stage.value / maxVal) * 100, 3)}%` }}>
                                  <span className="text-white text-xs font-bold">{stage.value.toLocaleString('en-IN')}</span>
                                </div>
                              </div>
                              <div className="w-16 text-right text-xs text-slate-500">{pct}%</div>
                            </div>
                          );
                        })}
                        <div className="text-xs text-slate-500 mt-2">Data from WTF CRM &bull; Updated in real-time</div>
                      </div>
                    );
                  })() : (
                    <div className="text-center py-12 text-slate-500">
                      <Layers className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <div className="text-sm">Loading funnel data...</div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════════════
              TAB 5: HOT LEADS (visited but not converted)
              ═══════════════════════════════════════════════════════════════════════ */}
          {activeMainTab === 'hotleads' && (() => {
            const filteredHotLeads = selectedAgent === 'all'
              ? hotLeads
              : hotLeads.filter(l => {
                  const agentName = (agentList.find(a => a.mobile === selectedAgent)?.name || selectedAgent).toLowerCase();
                  return l.agent?.toLowerCase().includes(agentName);
                });
            return (
            <Card className="bg-slate-900 border-slate-700/50">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <span className="text-sm font-medium">
                    {filteredHotLeads.length} Hot Leads — Visited but NOT Converted
                  </span>
                </div>
                <p className="text-xs text-slate-500">These leads visited a gym but haven't paid. Click a row to expand details.</p>
              </CardHeader>
              <CardContent>
                {filteredHotLeads.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-800/60 text-[10px] uppercase tracking-wider text-slate-400">
                        <th className="text-left px-3 py-2 w-6"></th>
                        <th className="text-left px-3 py-2">Lead Name</th>
                        <th className="text-left px-3 py-2">Phone</th>
                        <th className="text-left px-3 py-2">Gym</th>
                        <th className="text-left px-3 py-2">Agent</th>
                        <th className="text-left px-3 py-2">Visit Date</th>
                        <th className="text-center px-3 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredHotLeads.map((lead, i) => {
                        const isExpanded = expandedHotLead === i;
                        return (
                          <React.Fragment key={i}>
                            <tr
                              className="border-b border-slate-800 hover:bg-slate-800/30 cursor-pointer transition-colors"
                              onClick={() => setExpandedHotLead(isExpanded ? null : i)}
                            >
                              <td className="px-2 py-2 text-slate-500">
                                <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              </td>
                              <td className="px-3 py-2 text-slate-100 font-medium">{lead.name}</td>
                              <td className="px-3 py-2 text-slate-400 font-mono text-xs">{lead.mobile}</td>
                              <td className="px-3 py-2 text-slate-300">{lead.gym_name}</td>
                              <td className="px-3 py-2 text-slate-300">{lead.agent}</td>
                              <td className="px-3 py-2 text-slate-400 text-xs">{lead.visit_date || selectedDate}</td>
                              <td className="px-3 py-2 text-center">
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-500/10 text-red-400">
                                  NOT CONVERTED
                                </span>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan={7} className="bg-slate-800/30 px-6 py-4 border-b border-slate-700/50">
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                                    <div>
                                      <span className="text-slate-500 block">Lead Name</span>
                                      <span className="text-slate-100 font-medium">{lead.name || '--'}</span>
                                    </div>
                                    <div>
                                      <span className="text-slate-500 block">Phone</span>
                                      <span className="text-slate-200 font-mono">{lead.mobile || '--'}</span>
                                    </div>
                                    <div>
                                      <span className="text-slate-500 block">Gym</span>
                                      <span className="text-slate-200">{lead.gym_name || '--'}</span>
                                    </div>
                                    <div>
                                      <span className="text-slate-500 block">Agent</span>
                                      <span className="text-slate-200">{lead.agent || '--'}</span>
                                    </div>
                                    <div>
                                      <span className="text-slate-500 block">Visit Date</span>
                                      <span className="text-slate-200">{lead.visit_date || selectedDate}</span>
                                    </div>
                                    <div>
                                      <span className="text-slate-500 block">Next Follow-up</span>
                                      <span className="text-slate-200">{lead.next_followup_date || '--'}</span>
                                    </div>
                                    <div>
                                      <span className="text-slate-500 block">Status</span>
                                      <span className="text-red-400 font-medium">Visited, Not Converted</span>
                                    </div>
                                    <div>
                                      <span className="text-slate-500 block">CRM</span>
                                      <span className="text-slate-400 italic text-[10px]">View in WTF CRM (coming soon)</span>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400 opacity-60" />
                    <div className="text-sm text-emerald-400">All clear — no stale leads for {selectedDate}</div>
                  </div>
                )}
              </CardContent>
            </Card>
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
}
